"use client"

import React, { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { BreadcrumbsJsonLd } from '@/components/seo/Breadcrumbs'
import { motion } from 'framer-motion'
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Phone, Mail, Clock, CalendarPlus, Send } from 'lucide-react'
import { HowToBookSection } from '@/components/sections/how-to-book-section'
import { LocationsSection } from '@/components/sections/locations-section'
import { trackEvent } from '@/lib/analytics'

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  })

  const openBooking = (source?: string) => {
    if (source) {
      trackEvent('book_appointment_click', { location: source })
    }
    if ((window as any).openBookingModal) {
      ;(window as any).openBookingModal(source)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    // Handle form submission here
    console.log('Form submitted:', formData)
    trackEvent('contact_submit', { page: 'contact' })
  }

  return (
    <main className="min-h-screen bg-background">
      <Helmet>
        <title>Contact Raintree Chinese Medicine | Acupuncture in Noosa & Maroochydore | Sunshine Coast</title>
        <meta name="description" content="Contact Dr Renee Knott at Raintree Chinese Medicine clinics in Noosa and Maroochydore on the Sunshine Coast. Book acupuncture appointments or ask questions. Phone: +61 7 5240 9192." />
        <meta name="keywords" content="contact Raintree Chinese Medicine, acupuncture Noosa, acupuncture Maroochydore, Chinese medicine Sunshine Coast, Dr Renee Knott contact, book appointment Noosa, book appointment Maroochydore, Raintree Chinese Medicine phone, acupuncture Tewantin" />
        <link rel="canonical" href="https://raintreechinesemedicine.com.au/contact" />
        <meta property="og:title" content="Contact Raintree Chinese Medicine | Acupuncture in Noosa & Maroochydore | Sunshine Coast" />
        <meta property="og:description" content="Contact Dr Renee Knott at Raintree Chinese Medicine clinics in Noosa and Maroochydore on the Sunshine Coast. Book acupuncture appointments or ask questions. Phone: +61 7 5240 9192." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://raintreechinesemedicine.com.au/contact" />
        <meta property="og:image" content="https://tdjwqdpgnmzhfcqsfyjh.supabase.co/storage/v1/object/sign/images/general/Raintree-Chinese-Medicine-Renee.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8wYzVlMGM1NS1lYzI2LTRkMTYtOTc2MS1iZDQ1MWJhMGJiYjEiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJpbWFnZXMvZ2VuZXJhbC9SYWludHJlZS1DaGluZXNlLU1lZGljaW5lLVJlbmVlLnBuZyIsImlhdCI6MTc2NTE4MzkxOCwiZXhwIjoyMDIxNzkxOTE4fQ.-w4pg5Ka_Qt5M94A-DmuvJDiGP7a4DljS5sOLKvfAfE" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Raintree Chinese Medicine | Acupuncture in Noosa & Maroochydore | Sunshine Coast" />
        <meta name="twitter:description" content="Contact Dr Renee Knott at Raintree Chinese Medicine clinics in Noosa and Maroochydore on the Sunshine Coast. Book acupuncture appointments or ask questions. Phone: +61 7 5240 9192." />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ContactPage',
            name: 'Contact Raintree Chinese Medicine',
            description: 'Contact information for Raintree Chinese Medicine acupuncture and Chinese medicine clinics in Noosa and Maroochydore on the Sunshine Coast, Queensland.',
            url: 'https://raintreechinesemedicine.com.au/contact',
            mainEntity: {
              '@type': 'MedicalBusiness',
              name: 'Raintree Chinese Medicine',
              description: 'Acupuncture and Chinese medicine clinic offering women\'s health, fertility, pregnancy and long-term care services.',
              telephone: '+61752409192',
              email: 'renee@raintreechinesemedicine.com.au',
              address: [
                {
                  '@type': 'PostalAddress',
                  streetAddress: '31 Burgess Drive',
                  addressLocality: 'Tewantin',
                  addressRegion: 'QLD',
                  postalCode: '4565',
                  addressCountry: 'AU'
                },
                {
                  '@type': 'PostalAddress',
                  streetAddress: 'Ground Level 5/55 Plaza Parade',
                  addressLocality: 'Maroochydore',
                  addressRegion: 'QLD',
                  postalCode: '4558',
                  addressCountry: 'AU'
                }
              ],
              areaServed: [
                'Noosa',
                'Noosa Heads',
                'Noosaville',
                'Tewantin',
                'Maroochydore',
                'Sunshine Coast',
                'Buderim',
                'Bli Bli',
                'Cotton Tree',
                'Mooloolaba',
                'Cooroy'
              ],
              openingHoursSpecification: [
                {
                  '@type': 'OpeningHoursSpecification',
                  dayOfWeek: ['Monday', 'Wednesday', 'Friday'],
                  opens: '09:00',
                  closes: '17:00'
                },
                {
                  '@type': 'OpeningHoursSpecification',
                  dayOfWeek: 'Saturday',
                  opens: '09:00',
                  closes: '17:00',
                  description: 'By prior arrangement'
                }
              ],
              medicalSpecialty: [
                'Women\'s Health',
                'Fertility Treatment',
                'Pregnancy Care',
                'Acupuncture',
                'Chinese Medicine',
                'Hormonal Health',
                'IVF Support'
              ]
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: 'Home',
                  item: 'https://raintreechinesemedicine.com.au'
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: 'Contact',
                  item: 'https://raintreechinesemedicine.com.au/contact'
                }
              ]
            }
          })}
        </script>
      </Helmet>
      <h1 className="sr-only">Contact Raintree Chinese Medicine</h1>
      <BreadcrumbsJsonLd />
      <section className="bg-[rgb(132,137,116)] py-16 lg:py-40 px-4 lg:px-0">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto"
          >
            {/* Mobile: Stack layout */}
            <div className="lg:hidden text-center">
              <h1 className="text-2xl md:text-3xl mb-4 text-white">
                Contact
              </h1>
              <p className="text-white/90 sm:w-2/3 mx-auto mb-6 sm:mb-12 text-sm sm:text-base">
              Raintree Chinese Medicine has clinics in Tewantin and Maroochydore. For all bookings and free 15-minute phone consults, please use the online booking system. For non-urgent admin questions, you're welcome to phone or email.
              </p>
              <div className="flex flex-col gap-3 sm:w-1/2 mx-auto">
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('contact_hero_book')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book Appointment Online
                  </span>
                </Button>
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  asChild
                >
                  <a 
                    href="tel:+61752409192" 
                    onClick={() => trackEvent('call_click', { location: 'contact_hero' })}
                  >
                    <span className="inline-flex items-center gap-2">
                      <Phone className="h-4 w-4" aria-hidden="true" />
                      Phone Raintree
                    </span>
                  </a>
                </Button>
                <Button
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  asChild
                >
                  <a 
                    href="mailto:admin@raintreechinesemedicine.com.au"
                    onClick={() => trackEvent('email_click', { location: 'contact_hero' })}
                  >
                    <span className="inline-flex items-center gap-2">
                      <Send className="h-4 w-4" aria-hidden="true" />
                      Email Raintree
                    </span>
                  </a>
                </Button>
              </div>
            </div>

            {/* Desktop: Side-by-side layout */}
            <div className="hidden lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
              <div>
                <h1 className="text-3xl lg:text-4xl mb-4 text-white">
                  Contact
                </h1>
                <p className="text-white/90 text-base lg:text-lg">
                Raintree Chinese Medicine has clinics in Tewantin and Maroochydore. For all bookings and free 15-minute phone consults, please use the online booking system. For non-urgent admin questions, you're welcome to phone or email.
                </p>
              </div>
              <div className="flex flex-col gap-3 w-[60%] mx-auto">
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('contact_hero_book')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book Appointment Online
                  </span>
                </Button>
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  asChild
                >
                  <a 
                    href="tel:+61752409192" 
                    onClick={() => trackEvent('call_click', { location: 'contact_hero' })}
                  >
                    <span className="inline-flex items-center gap-2">
                      <Phone className="h-4 w-4" aria-hidden="true" />
                      Phone Raintree
                    </span>
                  </a>
                </Button>
                <Button
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  asChild
                >
                  <a 
                    href="mailto:admin@raintreechinesemedicine.com.au"
                    onClick={() => trackEvent('email_click', { location: 'contact_hero' })}
                  >
                    <span className="inline-flex items-center gap-2">
                      <Send className="h-4 w-4" aria-hidden="true" />
                      Email Raintree
                    </span>
                  </a>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <HowToBookSection />

      <LocationsSection onBook={(locationName) => {
        trackEvent('book_appointment_click', { location: `contact_locations_${locationName.toLowerCase()}` })
        openBooking(`contact_locations_${locationName.toLowerCase()}`)
      }} />

      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl mb-6 text-[#3f4141]">Contact Raintree</h2>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Card>
                <CardContent className="p-6">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Input
                        placeholder="Your Name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      />
                    </div>
                    <div>
                      <Input
                        type="email"
                        placeholder="Email Address"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                    </div>
                    <div>
                      <Input
                        type="tel"
                        placeholder="Phone Number"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      />
                    </div>
                    <div>
                      <Textarea
                        placeholder="Your Message"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        rows={4}
                      />
                    </div>
                    <Button 
                      type="submit"
                      className="w-full bg-gray-900 hover:bg-gray-700 text-white"
                    >
                      Send Message
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >


              <div className="flex items-start space-x-4">
                <Phone className="h-6 w-6 text-gray-900 flex-shrink-0" />
                <div>
                  <div className="text-lg font-medium mb-2">Phone</div>
                  <a href="tel:+61752409192" className="text-gray-600 hover:underline" onClick={() => trackEvent('call_click', { location: 'contact' })}>
                    +61&nbsp;7&nbsp;5240&nbsp;9192
                  </a> 
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <Mail className="h-6 w-6 text-gray-900 flex-shrink-0" />
                <div>
                  <div className="text-lg font-medium mb-2">Email</div>
                  <a href="mailto:admin@raintreechinesemedicine.com.au" className="text-gray-600 hover:underline">
                    admin@raintreechinesemedicine.com.au
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <Clock className="h-6 w-6 text-gray-900 flex-shrink-0" />
                <div>
                  <div className="text-lg font-medium mb-2">Opening Hours</div>
                  <div className="text-gray-600 space-y-2">
                    <div>
                      <b>Noosa Clinic:</b><br />
                      Monday, Wednesday & Friday: 9am to 5pm<br />
                      Saturday: by prior arrangement<br />
                    </div>
                    <div>
                      <b>Maroochydore Clinic:</b><br />
                      Tuesday and Thursday: 9am to 5pm<br />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

    </main>
  )
}